import { gstFromInclusive, isInterStateSupply } from "./gst";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

const ONES = [
  "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen",
];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function twoDigits(n) {
  if (n < 20) return ONES[n];
  return `${TENS[Math.floor(n / 10)]}${ONES[n % 10] ? ` ${ONES[n % 10]}` : ""}`.trim();
}

function sectionToWords(n, label) {
  if (!n) return "";
  return `${twoDigits(n)} ${label}`;
}

/** Indian numbering — rupees in words for credit note footer. */
export function amountInWordsIndian(amount) {
  const n = Math.round((Number(amount) || 0) * 100);
  if (n === 0) return "Zero Rupees Only";

  const rupees = Math.floor(n / 100);
  const paise = n % 100;

  const crore = Math.floor(rupees / 10000000);
  const lakh = Math.floor((rupees % 10000000) / 100000);
  const thousand = Math.floor((rupees % 100000) / 1000);
  const hundred = Math.floor((rupees % 1000) / 100);
  const rest = rupees % 100;

  const parts = [
    sectionToWords(crore, "Crore"),
    sectionToWords(lakh, "Lakh"),
    sectionToWords(thousand, "Thousand"),
    hundred ? `${ONES[hundred]} Hundred` : "",
    rest ? twoDigits(rest) : "",
  ].filter(Boolean);

  let words = parts.join(" ").trim() || "Zero";
  words += rupees === 1 ? " Rupee" : " Rupees";
  if (paise > 0) {
    words += ` and ${twoDigits(paise)} Paise`;
  }
  words += " Only";
  return words;
}

export function returnGstSummaryLines(saleReturn) {
  if ((saleReturn.igst_amount || 0) > 0.009) {
    return [{ label: "IGST (Credit)", amount: saleReturn.igst_amount }];
  }
  if ((saleReturn.cgst_amount || 0) > 0.009 || (saleReturn.sgst_amount || 0) > 0.009) {
    return [
      { label: "CGST (Credit)", amount: saleReturn.cgst_amount || 0 },
      { label: "SGST (Credit)", amount: saleReturn.sgst_amount || 0 },
    ];
  }
  if ((saleReturn.tax_amount || 0) > 0.009) {
    return [{ label: "GST (Credit)", amount: saleReturn.tax_amount }];
  }
  return [];
}

export function returnLineItems(saleReturn, priceIncludesGst, storeState, customerState) {
  const interState = isInterStateSupply(storeState, customerState);

  return (saleReturn.items || []).map((it, index) => {
    const lineTotal = Number(it.line_total) || 0;
    const rate = Number(it.gst_percent ?? 5) || 0;
    const qty = Number(it.quantity) || 0;
    const taxable = priceIncludesGst
      ? round2(lineTotal - gstFromInclusive(lineTotal, rate))
      : round2((Number(it.unit_price) || 0) * qty - (Number(it.discount) || 0));
    const gstAmt = priceIncludesGst
      ? round2(gstFromInclusive(lineTotal, rate))
      : round2(taxable * (rate / 100));
    const cgst = interState ? 0 : round2(gstAmt / 2);
    const sgst = interState ? 0 : round2(gstAmt - cgst);
    const igst = interState ? gstAmt : 0;
    const unitPriceBeforeTax = qty > 0 ? round2(taxable / qty) : 0;

    return {
      sr: index + 1,
      name: it.product_name,
      barcode: it.barcode,
      hsn_code: it.hsn_code || "",
      qty,
      unitPriceBeforeTax,
      rate,
      taxable,
      cgst,
      sgst,
      igst,
      lineTotal,
    };
  });
}

export function refundMethodLabel(method) {
  const m = String(method || "").toLowerCase();
  if (m === "cash") return "Cash";
  if (m === "upi") return "UPI";
  if (m === "card") return "Card";
  if (m === "adjust_balance") return "Adjusted against invoice balance";
  return method ? String(method).replace(/_/g, " ") : "—";
}

export function returnReasonText(saleReturn) {
  const notes = saleReturn?.notes?.trim();
  if (notes) return notes;
  return "Sales return of goods — credit note issued under Section 34 of CGST Act, 2017";
}
